import { describeMutationError } from "@api/mutationErrors";
import { ClinicAuthError } from "@auth/clinicAuthClient";
import { useIncomingCallStore } from "@notifications/incomingCallStore";
import {
  describeRefusal,
  isRetryable,
  OutboundCallError,
  type OutboundDestination,
  startOutboundCall,
} from "@phone/outboundCallsClient";
import { callOutcome } from "@phone/phoneCallOutcome";
import { fetchPhoneCall, type PhoneCall } from "@phone/phoneCallsClient";
import {
  currentPhoneCallsSession,
  patchPhoneCall,
  type PhoneCallsSession,
} from "@phone/phoneCallsQueries";
import { markPlacedCall } from "@phone/softphone";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

/** Only the moments before admission; the corner slip takes over after. */
type OutboundAttempt =
  | { phase: "idle" }
  | { phase: "dialing" }
  /** After a 503 `dispatch_unknown`. */
  | { phase: "checking" }
  /** `resumable`: outcome unknown, so "Try again" replays under the same key. */
  | {
      phase: "refused";
      message: string;
      retryable: boolean;
      resumable: boolean;
    };

interface OutboundInput {
  endpointUuid: string;
  destination: OutboundDestination;
  browser: boolean;
  idempotencyKey: string;
}

const CHECK_EVERY_MS = 3_000;
const CHECK_ATTEMPTS = 10;

/** Safe: TanStack Query retries with the same variables, so the idempotency key never changes. */
const UNCERTAIN_RETRY_ATTEMPTS = 2;
const UNCERTAIN_RETRY_DELAY_MS = 1_500;

/** A lost response, not an answer: brian sends every named refusal with a body, and those are never retried blind. */
const isUncertainFailure = (error: unknown): boolean => {
  if (error instanceof OutboundCallError) return false;
  if (!(error instanceof ClinicAuthError)) return false;
  return (
    error.code === "network_error" ||
    (error.code === "unknown" &&
      (error.status === undefined || error.status >= 500))
  );
};

const endedAttemptMessage = (call: PhoneCall): string =>
  `That call already ended — ${callOutcome(call).label}.`;

/**
 * The idempotency key names the attempt, not the submission: brian hands back
 * the established call for a repeated key. A lost response retries under the
 * same key; `dispatch_rejected` offers a retry with a new one; `dispatch_unknown`
 * reads the call by uuid. A success can replay an already-ended call, which
 * shows its outcome rather than a slip.
 */
export const useOutboundCall = ({
  onAdmitted,
}: {
  /** A host that opened this in a dialog closes it here. */
  onAdmitted?: () => void;
} = {}) => {
  const queryClient = useQueryClient();
  const raise = useIncomingCallStore((s) => s.raise);
  const [attempt, setAttempt] = useState<OutboundAttempt>({ phase: "idle" });
  const disposed = useRef(false);
  useEffect(() => {
    disposed.current = false;
    return () => {
      disposed.current = true;
    };
  }, []);

  /** Outcome still unknown after the automatic retries; "Try again" replays exactly this. */
  const unresolved = useRef<OutboundInput | null>(null);

  const attemptSession = useRef<PhoneCallsSession>(currentPhoneCallsSession());

  const admitted = (call: PhoneCall, browser: boolean) => {
    if (browser) markPlacedCall(call.id);
    raise(call);
    patchPhoneCall(queryClient, call, attemptSession.current);
    if (!disposed.current) setAttempt({ phase: "idle" });
    if (!disposed.current) onAdmitted?.();
  };

  const refuse = (message: string, retryable: boolean, resumable = false) => {
    if (!resumable) unresolved.current = null;
    if (!disposed.current)
      setAttempt({ phase: "refused", message, retryable, resumable });
  };

  const settle = (call: PhoneCall, browser: boolean) => {
    unresolved.current = null;
    if (call.status === "ended") refuse(endedAttemptMessage(call), true);
    else admitted(call, browser);
  };

  const check = async (callUuid: string, browser: boolean) => {
    setAttempt({ phase: "checking" });
    for (let tries = 0; tries < CHECK_ATTEMPTS; tries += 1) {
      await new Promise((resolve) => setTimeout(resolve, CHECK_EVERY_MS));
      if (disposed.current) return;
      let call: PhoneCall;
      try {
        call = await fetchPhoneCall(callUuid);
      } catch {
        continue;
      }
      settle(call, browser);
      return;
    }
    refuse("Brian couldn't confirm the call. Check the call log.", true, true);
  };

  const mutation = useMutation({
    mutationFn: (input: OutboundInput) => startOutboundCall(input),
    retry: (failureCount, error) =>
      failureCount < UNCERTAIN_RETRY_ATTEMPTS && isUncertainFailure(error),
    retryDelay: UNCERTAIN_RETRY_DELAY_MS,
    meta: { handled: true },
    onMutate: () => {
      attemptSession.current = currentPhoneCallsSession();
      setAttempt({ phase: "dialing" });
    },
    onSuccess: (call, input) => settle(call, input.browser),
    onError: (error, input) => {
      if (error instanceof OutboundCallError) {
        if (error.refusal === "dispatch_unknown" && error.phoneCallUuid) {
          unresolved.current = input;
          // The kept claim may already be ringing this headset.
          if (input.browser) markPlacedCall(error.phoneCallUuid);
          void check(error.phoneCallUuid, input.browser);
          return;
        }
        refuse(describeRefusal(error.refusal), isRetryable(error.refusal));
        return;
      }
      if (isUncertainFailure(error)) unresolved.current = input;
      refuse(
        describeMutationError(error, "place the call"),
        true,
        isUncertainFailure(error),
      );
    },
  });

  return {
    attempt,
    /** Mints a new idempotency key, including for an intentional redial. */
    start: (
      endpointUuid: string,
      destination: OutboundDestination,
      { browser }: { browser: boolean },
    ) => {
      unresolved.current = null;
      mutation.mutate({
        endpointUuid,
        destination,
        browser,
        idempotencyKey: crypto.randomUUID(),
      });
    },
    retry: (): boolean => {
      const input = unresolved.current;
      if (!input) return false;
      mutation.mutate(input);
      return true;
    },
    reset: () => setAttempt({ phase: "idle" }),
  };
};
